/**
 * ListingWizard Component
 *
 * Multi-step wizard for creating/editing listings.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PropertyBasics,
  LocationStep,
  AmenitiesStep,
  PhotosStep,
  PricingStep,
  BookingSettings,
  HouseRules,
  ReviewPublish,
} from './steps';
import { useListingForm, ListingFormData } from '../hooks';

interface ListingWizardProps {
  initialData?: Partial<ListingFormData>;
  listingId?: string;
  mode?: 'create' | 'edit';
}

interface WizardStep {
  id: string;
  title: string;
  shortTitle: string;
}

const STEPS: WizardStep[] = [
  { id: 'basics', title: 'Property Basics', shortTitle: 'Basics' },
  { id: 'location', title: 'Location', shortTitle: 'Location' },
  { id: 'amenities', title: 'Amenities', shortTitle: 'Amenities' },
  { id: 'photos', title: 'Photos', shortTitle: 'Photos' },
  { id: 'pricing', title: 'Pricing', shortTitle: 'Pricing' },
  { id: 'booking', title: 'Booking Settings', shortTitle: 'Booking' },
  { id: 'rules', title: 'House Rules', shortTitle: 'Rules' },
  { id: 'review', title: 'Review & Publish', shortTitle: 'Review' },
];

export const ListingWizard: React.FC<ListingWizardProps> = ({
  initialData,
  listingId,
  mode = 'create',
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  // Id of the document being written. On create it is empty until the first
  // save returns one; without this every auto-save would insert a new listing.
  const [docId, setDocId] = useState<string | undefined>(listingId);

  const {
    formData,
    isDirty,
    isSaving,
    error,
    updateFormData,
    updateLocation,
    updatePricing,
    updateHouseRules,
    addPhoto,
    removePhoto,
    reorderPhotos,
    updatePhotoUrl,
    toggleAmenity,
    saveDraft,
    publish,
  } = useListingForm(initialData);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleNext = async () => {
    // Auto-save on step change
    try {
      const savedId = await saveDraft(docId);
      if (savedId) setDocId(savedId);
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      // Error is handled by the hook
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGoToStep = (step: number) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveDraft = async () => {
    try {
      const savedId = await saveDraft(docId);
      if (savedId) {
        setDocId(savedId);
        toast.success('Draft saved successfully!');
      } else {
        toast.error('Add a title before saving a draft.');
      }
    } catch {
      // Error is handled by the hook
    }
  };

  const handlePublish = async () => {
    try {
      const savedId = await publish(docId);
      setDocId(savedId);
      toast.success('Listing submitted! Our team will review it shortly.', {
        duration: 6000,
      });
      navigate('/host/listings', { state: { published: true } });
    } catch {
      // Error is handled by the hook
    }
  };

  const handleExit = () => {
    if (isDirty) {
      setShowExitConfirm(true);
    } else {
      navigate('/host/listings');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <PropertyBasics
            data={formData}
            onChange={updateFormData}
          />
        );
      case 1:
        return (
          <LocationStep
            data={formData}
            onChange={updateFormData}
            onLocationChange={updateLocation}
          />
        );
      case 2:
        return (
          <AmenitiesStep
            data={formData}
            onToggleAmenity={toggleAmenity}
          />
        );
      case 3:
        return (
          <PhotosStep
            data={formData}
            onAddPhoto={addPhoto}
            onRemovePhoto={removePhoto}
            onReorderPhotos={reorderPhotos}
            onUpdatePhotoUrl={updatePhotoUrl}
          />
        );
      case 4:
        return (
          <PricingStep
            data={formData}
            onPricingChange={updatePricing}
          />
        );
      case 5:
        return (
          <BookingSettings
            data={formData}
            onChange={updateFormData}
          />
        );
      case 6:
        return (
          <HouseRules
            data={formData}
            onChange={updateFormData}
            onHouseRulesChange={updateHouseRules}
          />
        );
      case 7:
        return (
          <ReviewPublish
            data={formData}
            onGoToStep={handleGoToStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleExit}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span>{mode === 'create' ? 'Exit' : 'Back to listings'}</span>
        </button>
        <h1 className="text-xl font-semibold text-gray-900">
          {mode === 'create' ? 'Create listing' : 'Edit listing'}
        </h1>
        <div className="w-20" /> {/* Spacer */}
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">
            Step {currentStep + 1} of {STEPS.length}
          </span>
          <span className="text-sm font-medium text-gray-900">
            {STEPS[currentStep].title}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        {/* Step indicators */}
        <div className="hidden md:flex justify-between mt-2">
          {STEPS.map((step, idx) => (
            <button
              key={step.id}
              onClick={() => idx < currentStep && handleGoToStep(idx)}
              disabled={idx > currentStep}
              className={`text-xs ${
                idx === currentStep
                  ? 'text-primary-600 font-medium'
                  : idx < currentStep
                  ? 'text-gray-600 hover:text-gray-900 cursor-pointer'
                  : 'text-gray-400'
              }`}
            >
              {step.shortTitle}
            </button>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
        </div>
      )}

      {/* Step Content */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 mb-8">
        {renderStep()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pb-8">
        <button
          onClick={handleBack}
          disabled={currentStep === 0}
          className="px-6 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>

        <div className="flex gap-3">
          <button
            onClick={handleSaveDraft}
            disabled={isSaving}
            className="px-6 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
          >
            {isSaving ? 'Saving...' : 'Save draft'}
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={handleNext}
              disabled={isSaving}
              className="px-6 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600"
            >
              {isSaving ? 'Saving...' : 'Next'}
            </button>
          ) : (
            <button
              onClick={handlePublish}
              disabled={isSaving}
              className="px-6 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600"
            >
              {isSaving ? 'Publishing...' : 'Publish listing'}
            </button>
          )}
        </div>
      </div>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Save your progress?
            </h3>
            <p className="text-gray-600 mb-6">
              You have unsaved changes. Would you like to save them before leaving?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/host/listings')}
                className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                Discard
              </button>
              <button
                onClick={async () => {
                  try {
                    await saveDraft(docId);
                  } catch {
                    // Error is surfaced by the hook; still let them leave.
                  }
                  navigate('/host/listings');
                }}
                className="flex-1 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600"
              >
                Save & exit
              </button>
            </div>
            <button
              onClick={() => setShowExitConfirm(false)}
              className="w-full py-3 text-gray-600 hover:text-gray-900 mt-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Icon
const ArrowLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

export default ListingWizard;
